import axios from 'axios';
import { getToken } from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with authentication
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Get current integrator configuration (API keys)
 */
export const fetchCustomIntegratorConfig = async () => {
    const response = await api.get('/organizations/integrations/info');
    return response.data;
};

export const fetchRetailerInstallations = async (organizationId) => {
    const response = await api.get('/organizations/integrations/installations', { params: organizationId ? { organization_id: organizationId } : {} });
    return response.data;
};

export const fetchWooCommerceCredentialOptions = async (organizationId) => {
    const response = await api.get('/organizations/integrations/woocommerce/credential-options', { params: organizationId ? { organization_id: organizationId } : {} });
    return response.data;
};

export const createWooCommerceCredentials = async (payload) => {
    const response = await api.post('/organizations/integrations/woocommerce/credentials', payload);
    return response.data;
};

export const createPlatformCredentials = async (platform, payload) => {
    if (!['woocommerce', 'shopware'].includes(platform)) throw new Error('Unsupported integration platform');
    const response = await api.post(`/organizations/integrations/${platform}/credentials`, payload);
    return response.data;
};

export const revokePendingWooCommerceCredentials = async (id, organizationId) => {
    const response = await api.delete(`/organizations/integrations/woocommerce/credentials/${id}`, { params: organizationId ? { organization_id: organizationId } : {} });
    return response.data;
};

export const revokePendingPlatformCredentials = async (platform, id, organizationId) => {
    if (!['woocommerce', 'shopware'].includes(platform)) throw new Error('Unsupported integration platform');
    const response = await api.delete(`/organizations/integrations/${platform}/credentials/${id}`, { params: organizationId ? { organization_id: organizationId } : {} });
    return response.data;
};

/**
 * Rotate API credentials
 */
export const rotateCredentials = async () => {
    const response = await api.post('/organizations/integrations/credentials/rotate');
    return response.data;
};

/**
 * Get all webhooks
 */
export const fetchWebhooks = async () => {
    const response = await api.get('/organizations/integrations/webhooks');
    return response.data;
};

/**
 * Get single webhook
 */
export const fetchWebhook = async (webhookId) => {
    const response = await api.get(`/organizations/integrations/webhooks/${webhookId}`);
    return response.data;
};

/**
 * Create new webhook
 */
export const createWebhook = async (webhookData) => {
    const response = await api.post('/organizations/integrations/webhooks', {
        url: webhookData.url,
        events: webhookData.events,
        active: webhookData.active,
    });
    return response.data;
};

/**
 * Update webhook
 */
export const updateWebhook = async (webhookId, webhookData) => {
    const response = await api.put(`/organizations/integrations/webhooks/${webhookId}`, {
        url: webhookData.url,
        events: webhookData.events,
        active: webhookData.active,
    });
    return response.data;
};

/**
 * Delete webhook
 */
export const deleteWebhook = async (webhookId) => {
    const response = await api.delete(`/organizations/integrations/webhooks/${webhookId}`);
    return response.data;
};

/**
 * Test webhook delivery
 */
export const testWebhook = async (webhookId) => {
    const response = await api.post(`/organizations/integrations/webhooks/${webhookId}/test`);
    return response.data;
};

/**
 * Regenerate webhook secret
 */
export const regenerateWebhookSecret = async (webhookId) => {
    const response = await api.post(`/organizations/integrations/webhooks/${webhookId}/regenerate-secret`);
    return response.data;
};

/**
 * Get webhook delivery history
 */
export const fetchWebhookDeliveries = async (webhookId, limit = 50) => {
    const response = await api.get(`/organizations/integrations/webhooks/${webhookId}/deliveries`, {
        params: { limit },
    });
    return response.data;
};

/**
 * Get integration health status
 */
export const fetchIntegrationHealth = async () => {
    const response = await api.get('/organizations/integrations/health');
    return response.data;
};

/**
 * Get integration activity logs
 */
export const fetchIntegrationLogs = async (type = '', limit = 50) => {
    const response = await api.get('/organizations/integrations/logs', {
        params: { level: type, perPage: limit },
    });
    return response.data;
};

/**
 * Create a product
 */
export const createProduct = async (productData) => {
    const response = await api.post('/v1/integrator/products', productData);
    return response.data;
};

/**
 * Create products in batch
 */
export const createProductsBatch = async (products) => {
    const response = await api.post('/v1/integrator/products/batch', { products });
    return response.data;
};

/**
 * Get product by external ID
 */
export const fetchProduct = async (externalId) => {
    const response = await api.get(`/v1/integrator/products/${externalId}`);
    return response.data;
};

/**
 * List all products
 */
export const fetchProducts = async (page = 1, perPage = 50) => {
    const response = await api.get('/v1/integrator/products', {
        params: { page, per_page: perPage },
    });
    return response.data;
};

/**
 * Create a transaction
 */
export const createTransaction = async (transactionData) => {
    const response = await api.post('/v1/integrator/transactions', transactionData);
    return response.data;
};

/**
 * Get transaction by external ID
 */
export const fetchTransaction = async (externalId) => {
    const response = await api.get(`/v1/integrator/transactions/${externalId}`);
    return response.data;
};

/**
 * Create or update a customer
 */
export const createCustomer = async (customerData) => {
    const response = await api.post('/v1/integrator/customers', customerData);
    return response.data;
};

/**
 * Get customer by external ID
 */
export const fetchCustomer = async (externalId) => {
    const response = await api.get(`/v1/integrator/customers/${externalId}`);
    return response.data;
};

/**
 * Get organization information
 */
export const fetchOrganization = async () => {
    const response = await api.get('/v1/integrator/organization');
    return response.data;
};

/**
 * Get list of venues
 */
export const fetchVenues = async () => {
    const response = await api.get('/v1/integrator/venues');
    return response.data;
};

export default {
    fetchCustomIntegratorConfig,
    fetchWooCommerceCredentialOptions,
    createWooCommerceCredentials,
    revokePendingWooCommerceCredentials,
    rotateCredentials,
    fetchWebhooks,
    fetchWebhook,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    regenerateWebhookSecret,
    fetchWebhookDeliveries,
    fetchIntegrationHealth,
    fetchIntegrationLogs,
    createProduct,
    createProductsBatch,
    fetchProduct,
    fetchProducts,
    createTransaction,
    fetchTransaction,
    createCustomer,
    fetchCustomer,
    fetchOrganization,
    fetchVenues,
};
